import React from "react";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { colors } from "@/lib/theme";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Bucket a calendar block onto the VIEWER's local day. Date-only (all-day) strings
// have no timezone, so keep them as-is to avoid rolling backward across midnight.
const localDayOf = (s) => {
  if (!s) return "";
  const str = String(s);
  if (str.length <= 10) return str.slice(0, 10);
  const d = new Date(str);
  return isNaN(d.getTime()) ? str.slice(0, 10) : isoOf(d);
};
const OVERLAY = "#8A8B96";

const fmtRange = (start, end) => {
  const opt = { hour: "numeric", minute: "2-digit" };
  try {
    const allDay = String(start).length <= 10;
    if (allDay) return "All day";
    const s = new Date(start), e = new Date(end);
    return `${s.toLocaleTimeString([], opt)} – ${e.toLocaleTimeString([], opt)}`;
  } catch { return "Busy"; }
};

export function MonthCalendar({ cursor, setCursor, events, overlay = [], freeMembers = [], selectedDate, setSelectedDate, themeColor, themeOn, overlayLabel = "Shared busy", holidays = [], majorEvents = [] }) {
  const today = new Date();
  const todayIso = isoOf(today);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const byDate = {};
  events.forEach((e) => {
    (byDate[e.date] = byDate[e.date] || []).push(e);
  });

  const overlayByDate = {};
  overlay.forEach((b) => {
    const key = localDayOf(b.start);
    (overlayByDate[key] = overlayByDate[key] || []).push(b);
  });

  const HOLIDAY = "#E0A82E";
  const MAJOR = "#E5679E";
  const holidayByDate = {};
  holidays.forEach((h) => { const k = (h.date || "").slice(0, 10); if (k) (holidayByDate[k] = holidayByDate[k] || []).push(h); });
  const majorByDate = {};
  majorEvents.forEach((m) => { const k = (m.date || "").slice(0, 10); if (k) (majorByDate[k] = majorByDate[k] || []).push(m); });
  const hasExtras = holidays.length > 0 || majorEvents.length > 0;

  const leading = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = leading; i > 0; i--) cells.push({ date: new Date(year, month, 1 - i), other: true });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), other: false });
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    cells.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), other: true });
  }

  const selectedBlocks = selectedDate ? (overlayByDate[selectedDate] || []) : [];
  const hasOverlay = overlay.length > 0;

  return (
    <div className="rounded-xl p-3 mb-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>
          {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </span>
        <div className="flex items-center gap-1.5">
          <button data-testid="cal-today" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} className="rounded-full py-1 px-2.5" style={{ background: colors.surfaceTertiary }}>
            <span className="font-display font-medium text-[11px]" style={{ color: themeColor }}>Today</span>
          </button>
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-1.5 rounded-md" style={{ background: colors.surfaceTertiary }}>
            <ChevronLeft size={16} color={colors.onSurfaceSecondary} />
          </button>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-1.5 rounded-md" style={{ background: colors.surfaceTertiary }}>
            <ChevronRight size={16} color={colors.onSurfaceSecondary} />
          </button>
        </div>
      </div>

      <div className="flex mb-1">
        {WEEKDAYS.map((w, i) => (
          <span key={i} className="flex-1 text-center font-mono2 font-medium text-[10px]" style={{ color: colors.faint }}>{w}</span>
        ))}
      </div>

      {hasExtras && (
        <div className="flex items-center gap-3 mb-1.5 px-0.5">
          <span className="flex items-center gap-1"><span style={{ width: 6, height: 6, borderRadius: 3, background: "#E0A82E" }} /><span className="font-display text-[10px]" style={{ color: colors.faint }}>Holiday</span></span>
          <span className="flex items-center gap-1"><span style={{ width: 6, height: 6, borderRadius: 3, background: "#E5679E" }} /><span className="font-display text-[10px]" style={{ color: colors.faint }}>Major event</span></span>
        </div>
      )}

      <div className="flex flex-wrap">
        {cells.map((cell, i) => {
          const iso = isoOf(cell.date);
          const dayEvents = byDate[iso] || [];
          const dayOverlay = overlayByDate[iso] || [];
          const isToday = iso === todayIso;
          const isSel = iso === selectedDate;
          return (
            <button
              key={iso}
              data-testid={`cal-cell-${iso}`}
              onClick={() => setSelectedDate(isSel ? null : iso)}
              className="flex flex-col items-center pt-1 gap-[3px] rounded-md"
              style={{
                width: `${100 / 7}%`,
                aspectRatio: "1 / 1",
                border: isSel ? `1.5px solid ${themeColor}` : "1.5px solid transparent",
                background: isSel ? themeColor + "14" : "transparent",
              }}
            >
              <span className="w-6 h-6 rounded-full flex items-center justify-center font-mono2 text-[12px]"
                style={{ background: isToday ? themeColor : "transparent", color: isToday ? themeOn : cell.other ? colors.faint : colors.onSurfaceSecondary, fontWeight: isToday ? 700 : 500 }}>
                {cell.date.getDate()}
              </span>
              <span className="flex gap-[2px] items-center" style={{ height: 5 }}>
                {dayEvents.slice(0, 3).map((e) => (
                  <span key={e.id} style={{ width: 4, height: 4, borderRadius: 2, background: themeColor }} />
                ))}
                {dayOverlay.length > 0 && (
                  <span
                    data-testid={`cal-overlay-dot-${iso}`}
                    style={{ width: 4, height: 4, borderRadius: 2, background: OVERLAY, opacity: 0.9, border: `0.5px solid ${colors.surfaceSecondary}` }}
                  />
                )}
                {holidayByDate[iso] && <span data-testid={`cal-holiday-dot-${iso}`} style={{ width: 4, height: 4, borderRadius: 2, background: HOLIDAY }} />}
                {majorByDate[iso] && <span data-testid={`cal-major-dot-${iso}`} style={{ width: 4, height: 4, borderRadius: 2, background: MAJOR }} />}
              </span>
            </button>
          );
        })}
      </div>

      {selectedDate && (holidayByDate[selectedDate] || majorByDate[selectedDate]) && (
        <div data-testid="cal-extras-strip" className="mt-2 pt-2.5 flex flex-col gap-1.5" style={{ borderTop: `1px solid ${colors.border}` }}>
          {(holidayByDate[selectedDate] || []).map((h, i) => (
            <div key={`h${i}`} className="flex items-center gap-2">
              <span style={{ width: 6, height: 6, borderRadius: 3, background: HOLIDAY }} />
              <span className="font-display text-[12.5px] flex-1 truncate" style={{ color: colors.onSurfaceSecondary }}>{h.name}</span>
              <span className="font-display text-[9.5px] px-1.5 py-0.5 rounded-full" style={{ background: "#33290f", color: HOLIDAY }}>Holiday</span>
            </div>
          ))}
          {(majorByDate[selectedDate] || []).map((m, i) => (
            <div key={`m${i}`} className="flex items-center gap-2">
              <span style={{ width: 6, height: 6, borderRadius: 3, background: MAJOR }} />
              <span className="font-display text-[12.5px] flex-1 truncate" style={{ color: colors.onSurfaceSecondary }}>{m.title}{m.venue ? ` · ${m.venue}` : ""}</span>
              <span className="font-display text-[9.5px] px-1.5 py-0.5 rounded-full" style={{ background: "#3a1f2e", color: MAJOR }}>Major</span>
            </div>
          ))}
        </div>
      )}

      {hasOverlay && selectedDate && (
        <div data-testid="cal-day-busy-strip" className="mt-2 pt-2.5" style={{ borderTop: `1px solid ${colors.border}` }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <span style={{ width: 6, height: 6, borderRadius: 3, background: OVERLAY }} />
            <span className="font-display font-medium text-[10.5px] uppercase tracking-wide" style={{ color: colors.muted }}>{overlayLabel}</span>
          </div>
          {selectedBlocks.length === 0 ? (
            <p className="font-display text-[12px]" style={{ color: colors.faint }}>No one shared busy time this day.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {selectedBlocks.map((b, i) => {
                const isFree = (freeMembers || []).includes(b.user_id);
                return (
                  <div key={`${b.user_id}-${b.start}-${i}`} data-testid="cal-busy-block" className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
                    style={{ background: "repeating-linear-gradient(45deg, #22232E, #22232E 7px, #1E1F29 7px, #1E1F29 14px)", opacity: isFree ? 0.6 : 1 }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        {b.tier === "busy_only" && <Lock size={10} color={colors.muted} />}
                        <span className="font-display font-medium text-[12px] truncate" style={{ color: colors.onSurfaceSecondary }}>
                          {b.title || "Busy"}
                        </span>
                      </div>
                      <span className="font-display text-[10.5px]" style={{ color: colors.faint }}>
                        {b.member?.display_name} · {fmtRange(b.start, b.end)}
                      </span>
                    </div>
                    {isFree && (
                      <span className="font-display font-bold text-[9px] rounded-full px-1.5 py-0.5 shrink-0" style={{ background: "#12301F", color: "#3FBF7F" }}>
                        said free
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
